// Chakra Imports
import {
  Box,
  Button,
  Flex,
  useDisclosure,
} from "@chakra-ui/react";

import { ApiListUsers } from "api/auth/management/user";

import { useLoading } from "contexts/LoadingContext";
import { useEffect, useState, useMemo } from "react";

import { ApiListGroups } from "api/auth/management/group";

import { MdAddCircle } from "react-icons/md";

import UserTable from "./components/UserTable";
import GroupTable, { GroupManagementModal } from "./components/GroupTable";
import Card from 'components/card/Card';
import { SelectButtonGroup } from "components/toggle/LabeledToggle";
import { ActionButton } from "../simulator/components/ActionButton";

import { ManagementModalProps } from "./components/BaseTable";
import { UserManagementModal } from "./components/UserTable";
import { ApiListPermissions } from "api/auth/management/permission";
import { RawPermission } from "api/types";

import { useAuth } from "contexts/AuthProvider";

const CreateModal = (props: { modalComponent: any, modalProps: ManagementModalProps, disabled: boolean }) => {

  const { modalComponent, modalProps } = props
  const disclosure = useDisclosure()

  const Modal = modalComponent
  modalProps.disclosure = disclosure

  return (
    <>
      <ActionButton onClick={() => disclosure.onOpen()} icon={MdAddCircle} text="Agregar Registro" buttonProps={{ mt: { base: '20px', md: '0px' }, w: { base: '100%', md: '20vw' } }} />
      <Modal {...modalProps} />
    </>
  )
}

export default function UserManager() {

  const { user } = useAuth()
  const canChangeGroups = user.checkPermission('Auth', 'change_group') || user.is_staff
  const canChangeUsers = user.checkPermission('Custom Auth', 'change_user') || user.is_staff

  const [users, setUsers] = useState([])
  const [groups, setGroups] = useState([])
  const [groupOptions, setGroupOptions] = useState([])
  const [permissionOptions, setPermissionOptions] = useState([])
  const [viewMode, setViewMode] = useState((canChangeUsers&&canChangeGroups)||canChangeUsers?'users':'groups')
  const [groupTableKey, setGroupKey] = useState(Math.random())
  const [userTableKey, setUserKey] = useState(Math.random())
  const modalComponent = viewMode === 'users' ? UserManagementModal : GroupManagementModal

  const { show, hide } = useLoading()

  const handleViewSwitchToggle = (value: string) => {
    setViewMode(value);
  };

  const getUsers = async () => {
    show()
    await ApiListUsers().then(
      (response) => setUsers(response.data)
    )
    hide()
  }

  const getGroups = async () => {
    show()
    await ApiListGroups().then(
      (response) => {
        let data: any[] = response.data
        setGroups(data)
        // API devuelve `type` como string (p. ej. "0" = GroupTypes.USERS), no número.
        const isUserGroup = (g: { type?: string | number }) =>
          g.type === 0 || String(g.type) === "0"
        setGroupOptions(
          data
            .filter(isUserGroup)
            .map((g) => ({ label: g.name, value: g.name }))
        )
      }
    )
    hide()
  }

  const getPermissions = async () => {
    show()
    await ApiListPermissions().then((response) => {
      let data: RawPermission[] = response.data
      setPermissionOptions(data.map((p) => ({ label: `${p.app} - ${p.name}`, value: p.codename })))
    })
    hide()
  }

  const MemoizedUserTable = useMemo(() => {
    return users.length !== 0 ? <UserTable data={users} options={[{ for: "groups", options: groupOptions }, { for: "permissions", options: permissionOptions }]} key={userTableKey} reloader={setUserKey} /> : null
  }, [users, groupOptions, permissionOptions, userTableKey]);

  const MemoizedGroupTable = useMemo(() => {
    return groups.length !== 0 ? <GroupTable data={groups} options={[{ for: "permissions", options: permissionOptions }]} key={groupTableKey} reloader={setGroupKey} /> : null
  }, [groups]);

  useEffect(() => { getUsers(); getGroups(); getPermissions() }, [groupTableKey, userTableKey])

  return (
    <Box pt={{ base: "95px", md: "80px" }}>
      <Card py={2} px={'1vw'} mb='2vh'>
        <Flex
          w="100%"
          justifyContent={{ md: "space-between", base: "center" }}
          direction={{ base: "column", md: "row" }}
        >
          <Flex
            w={{ base: "auto", lg: "30%" }}
            alignItems="center"
            justifyContent='flex-start'
          >
            { canChangeUsers || canChangeGroups  ? <SelectButtonGroup onChange={handleViewSwitchToggle} value={viewMode} groupProps={{ isAttached: true, variant: 'brand', size: 'sm', w: { base: '100%', lg: '20vw' } }}>
                {canChangeUsers ? <Button value={'users'} w="50%" fontSize={{ base: '12px', md: '12px', lg: "sm" }}>Usuarios</Button> : <></>}
                {canChangeGroups ? <Button value={'groups'} w="50%" fontSize={{ base: '12px', md: '12px', lg: "sm" }}>Grupos</Button> : <></>}
            </SelectButtonGroup> : null }
          </Flex>
          <Flex justifyContent='flex-end'>
            <CreateModal
              modalComponent={modalComponent}
              modalProps={{ mode: 'create', options: [{ for: 'groups', options: groupOptions }, { for: 'permissions', options: permissionOptions }], viewReloader: viewMode === 'users' ? setUserKey : setGroupKey }}
              disabled={false} />
          </Flex>
        </Flex>
      </Card>
      {viewMode === 'users' ? MemoizedUserTable : MemoizedGroupTable}
    </Box>
  )
}